import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Globe, X, ChevronLeft, ChevronRight, Menu, CalendarDays, User } from "lucide-react";
import BookingCalendar from "../components/BookingCalendar";
import BookingSummary from "../components/BookingSummary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = {
  en: { name: "English", flag: "🇬🇧" },
  it: { name: "Italiano", flag: "🇮🇹" },
  es: { name: "Español", flag: "🇪🇸" },
  fr: { name: "Français", flag: "🇫🇷" },
  de: { name: "Deutsch", flag: "🇩🇪" },
  pt: { name: "Português", flag: "🇵🇹" },
  ru: { name: "Русский", flag: "🇷🇺" },
  "zh-Hans": { name: "简体中文", flag: "🇨🇳" },
  ko: { name: "한국어", flag: "🇰🇷" },
  ja: { name: "日本語", flag: "🇯🇵" },
  ar: { name: "العربية", flag: "🇸🇦" },
  hi: { name: "हिन्दी", flag: "🇮🇳" }
};

const LANGUAGE_ORDER = ["en", "it", "es", "fr", "de", "pt", "ru", "zh-Hans", "ko", "ja", "ar", "hi"];

const TRANSLATIONS = {
  nav: {
    about: { en: "About", it: "Chi Siamo", es: "Nosotros", fr: "À Propos", de: "Über Uns", pt: "Sobre", ru: "О Нас", "zh-Hans": "关于我们", ko: "소개", ja: "紹介", ar: "من نحن", hi: "हमारे बारे में" },
    gallery: { en: "Gallery", it: "Galleria", es: "Galería", fr: "Galerie", de: "Galerie", pt: "Galeria", ru: "Галерея", "zh-Hans": "画廊", ko: "갤러리", ja: "ギャラリー", ar: "معرض", hi: "गैलरी" },
    contact: { en: "Contact", it: "Contatti", es: "Contacto", fr: "Contact", de: "Kontakt", pt: "Contato", ru: "Контакт", "zh-Hans": "联系", ko: "연락처", ja: "連絡先", ar: "اتصل", hi: "संपर्क" },
    booking: { en: "Booking", it: "Prenota", es: "Reservar", fr: "Réserver", de: "Buchen", pt: "Reservar", ru: "Бронирование", "zh-Hans": "预订", ko: "예약", ja: "予約", ar: "الحجز", hi: "बुकिंग" }
  },
  form: {
    name: { en: "Name *", it: "Nome *", es: "Nombre *", fr: "Nom *", de: "Name *", pt: "Nome *", ru: "Имя *", "zh-Hans": "姓名 *", ko: "이름 *", ja: "名前 *", ar: "الاسم *", hi: "नाम *" },
    email: { en: "Email *", it: "Email *", es: "Email *", fr: "Email *", de: "Email *", pt: "Email *", ru: "Email *", "zh-Hans": "邮箱 *", ko: "이메일 *", ja: "メール *", ar: "البريد *", hi: "ईमेल *" },
    phone: { en: "Phone", it: "Telefono", es: "Teléfono", fr: "Téléphone", de: "Telefon", pt: "Telefone", ru: "Телефон", "zh-Hans": "电话", ko: "전화", ja: "電話", ar: "الهاتف", hi: "फ़ोन" },
    message: { en: "Message", it: "Messaggio", es: "Mensaje", fr: "Message", de: "Nachricht", pt: "Mensagem", ru: "Сообщение", "zh-Hans": "留言", ko: "메시지", ja: "メッセージ", ar: "رسالة", hi: "संदेश" },
    checkin: { en: "Check-in Date *", it: "Data Check-in *", es: "Fecha Check-in *", fr: "Date d'arrivée *", de: "Anreisedatum *", pt: "Data Check-in *", ru: "Дата заезда *", "zh-Hans": "入住日期 *", ko: "체크인 날짜 *", ja: "チェックイン日 *", ar: "تاريخ الوصول *", hi: "चेक-इन तिथि *" },
    checkout: { en: "Check-out Date *", it: "Data Check-out *", es: "Fecha Check-out *", fr: "Date de départ *", de: "Abreisedatum *", pt: "Data Check-out *", ru: "Дата выезда *", "zh-Hans": "退房日期 *", ko: "체크아웃 날짜 *", ja: "チェックアウト日 *", ar: "تاريخ المغادرة *", hi: "चेक-आउट तिथि *" },
    adults: { en: "Number of Adults *", it: "Numero Adulti *", es: "Número de Adultos *", fr: "Nombre d'adultes *", de: "Anzahl Erwachsene *", pt: "Número de Adultos *", ru: "Количество взрослых *", "zh-Hans": "成人数量 *", ko: "성인 수 *", ja: "大人の人数 *", ar: "عدد البالغين *", hi: "वयस्कों की संख्या *" },
    children: { en: "Number of Children", it: "Numero Bambini", es: "Número de Niños", fr: "Nombre d'enfants", de: "Anzahl Kinder", pt: "Número de Crianças", ru: "Количество детей", "zh-Hans": "儿童数量", ko: "어린이 수", ja: "子供の人数", ar: "عدد الأطفال", hi: "बच्चों की संख्या" },
    childrenAges: { en: "Children's Ages (e.g., 3, 7, 12)", it: "Età dei Bambini (es. 3, 7, 12)", es: "Edades de los Niños", fr: "Âges des enfants", de: "Alter der Kinder", pt: "Idades das Crianças", ru: "Возраст детей", "zh-Hans": "儿童年龄", ko: "어린이 나이", ja: "子供の年齢", ar: "أعمار الأطفال", hi: "बच्चों की उम्र" },
    privacy: { en: "I accept the", it: "Accetto la", es: "Acepto la", fr: "J'accepte la", de: "Ich akzeptiere die", pt: "Eu aceito a", ru: "Я принимаю", "zh-Hans": "我接受", ko: "동의합니다", ja: "同意します", ar: "أقبل", hi: "मैं स्वीकार करता हूं" },
    privacyLink: { en: "Privacy Policy", it: "Privacy Policy", es: "Política de Privacidad", fr: "Politique de Confidentialité", de: "Datenschutzerklärung", pt: "Política de Privacidade", ru: "Политику конфиденциальности", "zh-Hans": "隐私政策", ko: "개인정보 처리방침", ja: "プライバシーポリシー", ar: "سياسة الخصوصية", hi: "गोपनीयता नीति" },
    submit: { en: "Send Booking Request", it: "Invia Richiesta Prenotazione", es: "Enviar Solicitud", fr: "Envoyer la Demande", de: "Anfrage Senden", pt: "Enviar Solicitação", ru: "Отправить Запрос", "zh-Hans": "发送预订请求", ko: "예약 요청 보내기", ja: "予約リクエストを送信", ar: "إرسال الطلب", hi: "बुकिंग अनुरोध भेजें" },
    sending: { en: "Sending...", it: "Invio...", es: "Enviando...", fr: "Envoi...", de: "Senden...", pt: "Enviando...", ru: "Отправка...", "zh-Hans": "发送中...", ko: "전송 중...", ja: "送信中...", ar: "جاري الإرسال...", hi: "भेज रहा है..." },
    success: { en: "Booking request sent!", it: "Richiesta inviata!", es: "Solicitud enviada!", fr: "Demande envoyée!", de: "Anfrage gesendet!", pt: "Solicitação enviada!", ru: "Запрос отправлен!", "zh-Hans": "请求已发送！", ko: "요청이 전송되었습니다!", ja: "リクエストが送信されました！", ar: "تم إرسال الطلب!", hi: "बुकिंग अनुरोध भेजा गया!" }
  },
  gallery: {
    title: { en: "The Gallery", it: "La Galleria", es: "La Galería", fr: "La Galerie", de: "Die Galerie", pt: "A Galeria", ru: "Галерея", "zh-Hans": "画廊", ko: "갤러리", ja: "ギャラリー", ar: "المعرض", hi: "गैलरी" },
    explore: { en: "Explore", it: "Esplora", es: "Explorar", fr: "Explorer", de: "Entdecken", pt: "Explorar", ru: "Исследуйте", "zh-Hans": "探索", ko: "탐색", ja: "探索", ar: "استكشف", hi: "अन्वेषण" }
  },
  booking: {
    title: { en: "Check Availability", it: "Verifica Disponibilità", es: "Consultar Disponibilidad", fr: "Vérifier la Disponibilité", de: "Verfügbarkeit prüfen", pt: "Verificar Disponibilidade", ru: "Проверить наличие", "zh-Hans": "查看可用性", ko: "예약 가능 여부 확인", ja: "空き状況を確認", ar: "التحقق من التوفر", hi: "उपलब्धता जांचें" },
    subtitle: { en: "Book Your Stay", it: "Prenota il Tuo Soggiorno", es: "Reserva Tu Estancia", fr: "Réservez Votre Séjour", de: "Buchen Sie Ihren Aufenthalt", pt: "Reserve Sua Estadia", ru: "Забронируйте проживание", "zh-Hans": "预订您的住宿", ko: "숙박 예약", ja: "ご予約", ar: "احجز إقامتك", hi: "अपना प्रवास बुक करें" }
  },
  footer: {
    rights: { en: "All rights reserved.", it: "Tutti i diritti riservati.", es: "Todos los derechos reservados.", fr: "Tous droits réservés.", de: "Alle Rechte vorbehalten.", pt: "Todos os direitos reservados.", ru: "Все права защищены.", "zh-Hans": "版权所有。", ko: "모든 권리 보유.", ja: "全著作権所有。", ar: "جميع الحقوق محفوظة.", hi: "सर्वाधिकार सुरक्षित।" },
    privacy: { en: "Privacy Policy", it: "Privacy Policy", es: "Política de Privacidad", fr: "Politique de Confidentialité", de: "Datenschutz", pt: "Política de Privacidade", ru: "Политика конфиденциальности", "zh-Hans": "隐私政策", ko: "개인정보 처리방침", ja: "プライバシーポリシー", ar: "سياسة الخصوصية", hi: "गोपनीयता नीति" },
    legal: { en: "Legal Information", it: "Informazioni Legali", es: "Información Legal", fr: "Mentions Légales", de: "Impressum", pt: "Informações Legais", ru: "Правовая информация", "zh-Hans": "法律信息", ko: "법적 정보", ja: "法的情報", ar: "المعلومات القانونية", hi: "कानूनी जानकारी" }
  }
};

export default function HomePage() {
  // Initialize language from URL param or localStorage or default to 'en'
  const getInitialLanguage = () => {
    const validLangs = LANGUAGE_ORDER;
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang && validLangs.includes(urlLang)) {
      return urlLang;
    }
    const storedLang = localStorage.getItem('language');
    if (storedLang && validLangs.includes(storedLang)) {
      return storedLang;
    }
    return "en"; // Default to English
  };
  
  const [language, setLanguage] = useState(getInitialLanguage);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [sections, setSections] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [settings, setSettings] = useState({});
  const [legal, setLegal] = useState({});
  const [logoConfig, setLogoConfig] = useState({ logo_url: null, logo_size: 40, show_in_footer: true });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]); // SCOPED images for current lightbox
  const [showLegalModal, setShowLegalModal] = useState(null); // {type: 'policy'|'contact', item: {...}}
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", message: "",
    check_in: "", check_out: "", adults: 1, children: 0,
    children_ages: "", privacy_accepted: false, honeypot: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // LOADING STATE - Previene layout shift del footer
  const [isPageLoading, setIsPageLoading] = useState(true);
  
  // DEBUG state
  const [debugInfo, setDebugInfo] = useState({ raw: 0, filtered: 0, excluded: [] });
  
  // Booking calendar state
  const [calendarData, setCalendarData] = useState([]);
  const [calendarConfig, setCalendarConfig] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [selectedDates, setSelectedDates] = useState({ checkIn: null, checkOut: null });
  const [selectedGuests, setSelectedGuests] = useState({ adults: 2, children: 0, infants: 0, childrenAges: [] });

  useEffect(() => { fetchData(); fetchCalendar(); }, []);

  // ============================================================
  // ANCHOR LINK HANDLER - Case-insensitive navigation
  // ============================================================
  // Permette di usare #Bedroom, #BEDROOM, #bedroom, #Booking - tutti funzionano
  // L'utente può usare il nome che vede nel menu come riferimento
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        // Normalizza l'hash: rimuovi # , converti in lowercase, sostituisci spazi con -
        const normalizedHash = hash.substring(1).toLowerCase().replace(/\s+/g, '-');
        const targetElement = document.getElementById(normalizedHash);
        
        if (targetElement) {
          // Scrolla all'elemento con smooth behavior
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    // Gestisci hash all'avvio della pagina - solo al primo caricamento
    // Usa una flag per evitare scroll automatici indesiderati
    const hasScrolledOnce = sessionStorage.getItem('hasScrolledToHash');
    if (window.location.hash && !isPageLoading && !hasScrolledOnce) {
      sessionStorage.setItem('hasScrolledToHash', 'true');
      // Aspetta un po' per assicurarsi che il DOM sia completamente renderizzato
      setTimeout(handleHashChange, 300);
    }

    // Ascolta i cambiamenti dell'hash (per click su link)
    window.addEventListener('hashchange', handleHashChange);
    
    // Intercetta click su link interni con # per normalizzarli
    const handleLinkClick = (e) => {
      const target = e.target.closest('a[href^="#"], button[data-href^="#"]');
      if (target) {
        const href = target.getAttribute('href') || target.getAttribute('data-href');
        if (href && href.startsWith('#')) {
          e.preventDefault();
          const normalizedHash = href.substring(1).toLowerCase().replace(/\s+/g, '-');
          const targetElement = document.getElementById(normalizedHash);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Aggiorna l'URL senza ricaricare
            window.history.pushState(null, '', `#${normalizedHash}`);
          }
        }
      }
    };
    
    document.addEventListener('click', handleLinkClick);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      document.removeEventListener('click', handleLinkClick);
    };
  }, [isPageLoading]); // Riesegui quando isPageLoading cambia

  const fetchData = async () => {
    try {
      await axios.post(`${API}/init-site`);
      await axios.post(`${API}/init-booking-section`);
      const [sectionsRes, galleryRes, settingsRes, legalRes, logoRes] = await Promise.all([
        axios.get(`${API}/sections`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/legal`),
        axios.get(`${API}/logo-config`)
      ]);
      // DEBUG: Track raw data and filtering
      const rawSections = sectionsRes.data;
      const excluded = [];
      const filtered = rawSections.filter(s => {
        // Check is_active
        if (s.is_active === false) {
          excluded.push({ name: s.section_name, reason: "is_active=false" });
          return false;
        }
        // Check show_in_home
        if (s.show_in_home === false) {
          excluded.push({ name: s.section_name, reason: "show_in_home=false" });
          return false;
        }
        return true;
      }).sort((a, b) => (a.order || 0) - (b.order || 0));
      
      setDebugInfo({ raw: rawSections.length, filtered: filtered.length, excluded, rawData: rawSections });
      setSections(filtered);
      console.log("DEBUG SECTIONS:", { raw: rawSections.length, filtered: filtered.length, excluded, rawData: rawSections });
      setGallery(galleryRes.data);
      setLegal(legalRes.data);
      setLogoConfig(logoRes.data);
      const settingsMap = {};
      settingsRes.data.forEach(s => { settingsMap[s.category] = s.settings; });
      setSettings(settingsMap);
    } catch (error) { 
      console.error("Error fetching data:", error); 
    } finally {
      // IMPORTANTE: Imposta loading a false DOPO che i dati sono pronti
      setIsPageLoading(false);
    }
  };

  const fetchCalendar = async () => {
    setCalendarLoading(true);
    try {
      const response = await axios.get(`${API}/calendar`);
      setCalendarData(response.data.calendar || []);
      setCalendarConfig(response.data.config || {});
    } catch (error) {
      console.error("Error fetching calendar:", error);
    } finally {
      setCalendarLoading(false);
    }
  };

  const t = useCallback((category, key) => TRANSLATIONS[category]?.[key]?.[language] || TRANSLATIONS[category]?.[key]?.en || key, [language]);

  const getBlockContent = useCallback((block, field = "content") => {
    const data = block?.[field];
    return data?.[language] || data?.en || "";
  }, [language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!formData.privacy_accepted) {
      toast.error(language === "it" ? "Devi accettare la Privacy Policy" : "You must accept the Privacy Policy");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t("form", "success"));
      setFormData({ name: "", email: "", phone: "", message: "", check_in: "", check_out: "", adults: 1, children: 0, children_ages: "", privacy_accepted: false, honeypot: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  // LIGHTBOX SCOPED - Accetta l'array di immagini specifico del blocco
  const openLightbox = (index, images) => { 
    console.log(`LIGHTBOX OPEN: index=${index}, images count=${images?.length}`, images);
    setLightboxImages(images || []); 
    setLightboxIndex(index); 
    setLightboxOpen(true); 
    document.body.style.overflow = "hidden"; 
  };
  const closeLightbox = () => { setLightboxOpen(false); setLightboxImages([]); document.body.style.overflow = "auto"; };
  const navigateLightbox = (dir) => setLightboxIndex(prev => {
    const len = lightboxImages.length;
    if (len === 0) return 0;
    return dir === "next" ? (prev + 1) % len : (prev - 1 + len) % len;
  });

  const typography = settings.typography || {};

  // Get full image URL (prefix local uploads with backend URL)
  const getImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("/api/uploads/")) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  // Get text alignment class
  const getAlignClass = (align) => {
    if (align === "center") return "text-center mx-auto";
    if (align === "right") return "text-right ml-auto";
    return "text-left";
  };

  // Get image sizing style
  const getImageStyle = (sizing) => {
    if (sizing === "actual") return { objectFit: "none", maxWidth: "100%" };
    if (sizing === "contain") return { objectFit: "contain" };
    return { objectFit: "cover" };
  };

  // Get padding style for a block (internal spacing)
  const getBlockPaddingStyle = (block) => {
    const style = {};
    if (block.padding_top != null && block.padding_top > 0) {
      style.paddingTop = `${block.padding_top}px`;
    }
    if (block.padding_bottom != null && block.padding_bottom > 0) {
      style.paddingBottom = `${block.padding_bottom}px`;
    }
    return style;
  };

  // Get margin style for a block (external spacing between blocks)
  const getBlockMarginStyle = (block) => {
    const style = {};
    if (block.margin_top != null) {
      style.marginTop = `${block.margin_top}px`;
    }
    if (block.margin_bottom != null) {
      style.marginBottom = `${block.margin_bottom}px`;
    }
    return style;
  };

  // Render a single block
  const renderBlock = (block) => {
    const alignClass = getAlignClass(block.text_align);
    const paddingStyle = getBlockPaddingStyle(block);
    const marginStyle = getBlockMarginStyle(block);
    
    // Priority: Block override > Section style > Global typography
    // Block-level overrides take precedence over global typography settings
    const getTypographyStyle = (blockType) => {
      const defaults = {
        h1: { size: "clamp(2.5rem, 5vw, 4rem)", weight: "300", color: "#1C1917" },
        h2: { size: "clamp(1.8rem, 4vw, 2.8rem)", weight: "400", color: "#1C1917" },
        h3: { size: "clamp(1.4rem, 3vw, 2.1rem)", weight: "400", color: "#1C1917" },
        paragraph: { size: "1rem", weight: "400", color: "#78716C" }
      };
      
      // Priority chain: Block override > Global typography > Defaults
      const fontSize = block.font_size || typography[blockType]?.size || defaults[blockType].size;
      const fontWeight = block.font_weight || typography[blockType]?.weight || defaults[blockType].weight;
      const color = block.text_color || typography[blockType]?.color || defaults[blockType].color;
      
      return { fontSize, fontWeight, color };
    };

    // Wrapper function to apply padding and margin to any block
    const wrapWithSpacing = (element, skipDefaultMargin = false) => {
      // Check if block has any custom spacing defined
      const hasCustomPadding = block.padding_top != null || block.padding_bottom != null;
      const hasCustomMargin = block.margin_top != null || block.margin_bottom != null;
      const hasCustomSpacing = hasCustomPadding || hasCustomMargin;
      
      if (hasCustomSpacing) {
        // Combine padding and margin into one style object
        const combinedStyle = { ...paddingStyle, ...marginStyle };
        // Clone element and merge styles, also remove default mb-* classes
        const cleanedClassName = element.props.className?.replace(/\bmb-\d+\b/g, '').trim() || '';
        const mergedStyle = { ...element.props.style, ...combinedStyle };
        return React.cloneElement(element, { 
          key: block.block_id, 
          className: cleanedClassName,
          style: mergedStyle
        });
      }
      return element;
    };
    
    switch (block.block_type) {
      case "h1":
        return wrapWithSpacing(
          <h1 key={block.block_id} className={`font-heading leading-tight ${marginStyle.marginBottom == null ? 'mb-6' : ''} ${alignClass}`}
            style={getTypographyStyle("h1")}>
            {getBlockContent(block)}
          </h1>
        );
      case "h2":
        return wrapWithSpacing(
          <h2 key={block.block_id} className={`font-heading leading-tight ${marginStyle.marginBottom == null ? 'mb-4' : ''} ${alignClass}`}
            style={getTypographyStyle("h2")}>
            {getBlockContent(block)}
          </h2>
        );
      case "h3":
        return wrapWithSpacing(
          <h3 key={block.block_id} className={`font-heading leading-snug ${marginStyle.marginBottom == null ? 'mb-3' : ''} ${alignClass}`}
            style={getTypographyStyle("h3")}>
            {getBlockContent(block)}
          </h3>
        );
      case "paragraph":
        return wrapWithSpacing(
          <p key={block.block_id} className={`font-body leading-relaxed ${marginStyle.marginBottom == null ? 'mb-6' : ''} max-w-3xl ${alignClass}`}
            style={getTypographyStyle("paragraph")}>
            {getBlockContent(block)}
          </p>
        );
      case "image":
        // FAIL-SAFE: Se image_url è vuoto/null, non renderizzare il blocco (graceful skip)
        if (!block.image_url || block.image_url.trim() === "") {
          console.warn(`Block ${block.block_id}: skipped - no image_url`);
          return null;
        }
        const imgStyle = getImageStyle(block.image_sizing);
        // Handler per errori di caricamento immagine - nasconde l'immagine invece di mostrare icona rotta
        const handleImageError = (e) => {
          console.warn(`Image failed to load: ${block.image_url}`);
          e.target.style.display = 'none'; // Nascondi immagine rotta
          e.target.parentElement.style.display = 'none'; // Nascondi anche il container
        };
        // Only add mb-8 if no custom margin_bottom is set
        const imageMarginClass = block.margin_bottom != null ? '' : 'mb-8';
        if (block.image_layout === "full") {
          return wrapWithSpacing(
            <div key={block.block_id} className={`w-full ${imageMarginClass}`.trim()}>
              <img 
                src={getImageUrl(block.image_url)} 
                alt={getBlockContent(block, "image_alt")}
                className={`w-full object-cover ${block.image_sizing === "actual" ? "max-w-full h-auto mx-auto" : "h-[50vh] md:h-[70vh]"}`}
                style={imgStyle}
                onError={handleImageError}
                loading="lazy"
              />
            </div>
          );
        } else if (block.image_layout === "left") {
          return wrapWithSpacing(
            <div key={block.block_id} className={`w-full md:w-1/2 md:float-left md:mr-8 ${block.margin_bottom != null ? '' : 'mb-6'}`.trim()}>
              <img src={getImageUrl(block.image_url)} alt={getBlockContent(block, "image_alt")} className="w-full h-auto" style={imgStyle} onError={handleImageError} loading="lazy" />
            </div>
          );
        } else {
          return wrapWithSpacing(
            <div key={block.block_id} className={`w-full md:w-1/2 md:float-right md:ml-8 ${block.margin_bottom != null ? '' : 'mb-6'}`.trim()}>
              <img src={getImageUrl(block.image_url)} alt={getBlockContent(block, "image_alt")} className="w-full h-auto" style={imgStyle} onError={handleImageError} loading="lazy" />
            </div>
          );
        }
      case "button":
        const buttonAlignClass = block.button_align === "left" ? "text-left" : 
                                  block.button_align === "right" ? "text-right" : "text-center";
        return wrapWithSpacing(
          <div key={block.block_id} className={`mb-4 ${buttonAlignClass}`}>
            <a href={block.button_link || "#"} className={block.button_style === "primary" ? "btn-primary inline-block" : "btn-secondary inline-block"}>
              {getBlockContent(block, "button_text")}
            </a>
          </div>
        );
      case "spacer":
        return <div key={block.block_id} className="h-8 md:h-12" style={paddingStyle} />;
      case "gallery":
        const galleryTitle = block.gallery_title?.[language] || block.gallery_title?.en || "";
        const galleryDesc = block.gallery_description?.[language] || block.gallery_description?.en || "";
        const galleryImages = block.gallery_images || [];
        
        // FAIL-SAFE: Filtra solo immagini con URL valido
        const validGalleryImages = galleryImages.filter(img => img.url && img.url.trim() !== "");
        
        // Se non ha né immagini valide, né titolo, né descrizione, non renderizzare nulla
        if (validGalleryImages.length === 0 && !galleryTitle && !galleryDesc) {
          return null;
        }
        
        const layoutClass = block.gallery_layout === "slider" ? "flex gap-4 overflow-x-auto snap-x" :
                           block.gallery_layout === "columns-2" ? "grid grid-cols-1 sm:grid-cols-2 gap-4" :
                           block.gallery_layout === "columns-3" ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" :
                           block.gallery_layout === "columns-4" ? "grid grid-cols-2 md:grid-cols-4 gap-4" :
                           "grid grid-cols-2 md:grid-cols-4 gap-4"; // default: grid
        return wrapWithSpacing(
          <div key={block.block_id}>
            {galleryTitle && (
              <h3 className="font-heading text-2xl md:text-3xl mb-3 text-center"
                style={{ color: typography.h2?.color || "#1C1917" }}>
                {galleryTitle}
              </h3>
            )}
            {galleryDesc && (
              <p className="text-center text-[#78716C] mb-6 max-w-2xl mx-auto">{galleryDesc}</p>
            )}
            {validGalleryImages.length > 0 && (
              <div className={layoutClass}>
                {validGalleryImages.map((img, imgIdx) => (
                  <div key={img.image_id || imgIdx} className={`overflow-hidden cursor-pointer group ${block.gallery_layout === "slider" ? "flex-shrink-0 w-64 snap-center" : "aspect-square"}`}
                    onClick={() => {
                      // SCOPED: usa SOLO le immagini di questo blocco gallery
                      console.log(`Gallery block ${block.block_id}: opening image ${imgIdx} of ${validGalleryImages.length}`);
                      openLightbox(imgIdx, validGalleryImages);
                    }}>
                    <img 
                      src={img.url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${img.url}` : img.url}
                      alt={img.alt?.[language] || img.alt?.en || img.caption?.[language] || ""} 
                      loading="lazy"
                      className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${block.gallery_layout === "slider" ? "h-48" : ""}`}
                      onError={(e) => { e.target.parentElement.style.display = 'none'; }} // FAIL-SAFE: nascondi se errore
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  // Render a section - behavior based on BLOCKS, not section_type
  const renderSection = (section) => {
    const sectionId = section.section_name.toLowerCase().replace(/\s+/g, '-');
    
    // Sort blocks by order field to respect admin ordering
    const sortedBlocks = [...(section.blocks || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    console.log(`RENDERING SECTION: ${section.section_name}, order=${section.order}, blocks=${sortedBlocks.length}`, 
      sortedBlocks.map(b => ({ type: b.block_type, order: b.order })));

    // Check if section has booking form block (or is the booking section for backwards compat)
    const hasBookingBlock = sortedBlocks.some(b => b.block_type === "booking_form") || section.section_type === "booking";
    if (hasBookingBlock) {
      // Render all content blocks from builder (no hardcoded text)
      const contentBlocks = sortedBlocks.filter(b => !["booking_form"].includes(b.block_type));
      
      return (
        <section key={section.section_id} id="booking" className={section.padding || "py-24"}
          style={{ backgroundColor: section.background_color || "#FFFFFF", minHeight: "auto" }} data-testid="booking-section">
          <div className="max-w-screen-xl mx-auto px-6 md:px-12">
            {/* Content blocks from builder - rendered in order */}
            {contentBlocks.length > 0 && (
              <div className="text-center mb-12">
                {contentBlocks.map(block => renderBlock(block))}
              </div>
            )}
            
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Calendar */}
              <BookingCalendar
                language={language}
                calendarData={calendarData}
                config={calendarConfig}
                loading={calendarLoading}
                onDateSelect={setSelectedDates}
                onGuestsChange={setSelectedGuests}
              />
              
              {/* Summary & Form */}
              <div id="booking-summary">
                <BookingSummary
                  language={language}
                  checkIn={selectedDates.checkIn}
                  checkOut={selectedDates.checkOut}
                  adults={selectedGuests.adults}
                  children={selectedGuests.children}
                  infants={selectedGuests.infants}
                  childrenAges={selectedGuests.childrenAges}
                  onChildrenAgesChange={(newAges) => setSelectedGuests(prev => ({...prev, childrenAges: newAges}))}
                  config={calendarConfig}
                  onPrivacyClick={() => setShowPrivacyModal(true)}
                />
              </div>
            </div>
          </div>
        </section>
      );
    }

    // Contact section - CENTERED FORM
    if (section.section_type === "contact") {
      return (
        <section key={section.section_id} id="contact" className={section.padding || "py-24"}
          style={{ backgroundColor: section.background_color || "#FFFFFF" }} data-testid="contact-section">
          <div className="max-w-screen-xl mx-auto px-6 md:px-12">
            {/* Centered header */}
            <div className="text-center mb-12">
              <p className="font-body text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">{t("nav", "contact")}</p>
              {sortedBlocks.map(block => renderBlock(block))}
            </div>
            
            {/* Centered form */}
            <div className="max-w-2xl mx-auto">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <input type="text" name="honeypot" value={formData.honeypot} onChange={(e) => setFormData({...formData, honeypot: e.target.value})} style={{ display: "none" }} tabIndex={-1} />
                
                <div className="grid md:grid-cols-2 gap-6">
                  <input type="text" placeholder={t("form", "name")} value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="input-luxury" data-testid="contact-name" />
                  <input type="email" placeholder={t("form", "email")} value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="input-luxury" data-testid="contact-email" />
                </div>
                
                <input type="tel" placeholder={t("form", "phone")} value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="input-luxury" data-testid="contact-phone" />

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#78716C] mb-2 block">{t("form", "checkin")}</label>
                    <input type="date" value={formData.check_in} onChange={(e) => setFormData({...formData, check_in: e.target.value})} required className="input-luxury" data-testid="contact-checkin" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#78716C] mb-2 block">{t("form", "checkout")}</label>
                    <input type="date" value={formData.check_out} onChange={(e) => setFormData({...formData, check_out: e.target.value})} required className="input-luxury" data-testid="contact-checkout" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#78716C] mb-2 block">{t("form", "adults")}</label>
                    <select value={formData.adults} onChange={(e) => setFormData({...formData, adults: parseInt(e.target.value)})} className="input-luxury" data-testid="contact-adults">
                      {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#78716C] mb-2 block">{t("form", "children")}</label>
                    <select value={formData.children} onChange={(e) => setFormData({...formData, children: parseInt(e.target.value)})} className="input-luxury" data-testid="contact-children">
                      {[0,1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>

                {formData.children > 0 && (
                  <input type="text" placeholder={t("form", "childrenAges")} value={formData.children_ages} onChange={(e) => setFormData({...formData, children_ages: e.target.value})} className="input-luxury" data-testid="contact-children-ages" />
                )}

                <textarea placeholder={t("form", "message")} rows={4} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} className="input-luxury resize-none" data-testid="contact-message" />

                <div className="flex items-start gap-3 justify-center">
                  <input type="checkbox" id="privacy" checked={formData.privacy_accepted} onChange={(e) => setFormData({...formData, privacy_accepted: e.target.checked})} className="mt-1 w-5 h-5 accent-[#D4AF37]" required data-testid="contact-privacy" />
                  <label htmlFor="privacy" className="text-sm text-[#78716C]">
                    {t("form", "privacy")}{" "}
                    <button type="button" onClick={() => setShowPrivacyModal(true)} className="text-[#D4AF37] underline hover:text-[#1C1917]" data-testid="privacy-link">
                      {t("form", "privacyLink")}
                    </button> *
                  </label>
                </div>

                <div className="text-center">
                  <button type="submit" className="btn-primary" disabled={submitting} data-testid="contact-submit">
                    {submitting ? t("form", "sending") : t("form", "submit")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      );
    }

    // Check if this is the FIRST section with a full-width hero image
    // IMPORTANTE: Trova la prima sezione che CONTIENE un'immagine full-width
    // (non necessariamente la sezione con order più basso)
    const sortedSections = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));
    const firstSectionWithHeroImage = sortedSections.find(s => {
      const blocks = s.blocks || [];
      return blocks.some(b => b.block_type === "image" && b.image_layout === "full" && b.image_url && b.image_url.trim() !== "");
    });
    const isHeroSection = firstSectionWithHeroImage && section.section_id === firstSectionWithHeroImage.section_id;
    const imageBlock = sortedBlocks.find(b => b.block_type === "image" && b.image_layout === "full" && b.image_url && b.image_url.trim() !== "");
    
    if (isHeroSection && imageBlock) {
      const textBlocks = sortedBlocks.filter(b => b.block_type !== "image") || [];
      
      return (
        <section key={section.section_id} id={sectionId}>
          {/* 
            HERO CONTAINER - STRUTTURA ROBUSTA "A PROVA DI MODIFICHE"
            ============================================================
            - Il container ha SEMPRE height: 100vh (viewport height)
            - L'immagine usa object-fit: cover per riempire senza distorsione
            - Non usa calc(), padding dinamici o dipendenze da altri elementi
          */}
          <div className="hero-container relative w-full h-screen overflow-hidden">
            {/* Immagine Hero - SEMPRE full viewport */}
            <img 
              src={getImageUrl(imageBlock.image_url)} 
              alt={getBlockContent(imageBlock, "image_alt")}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
              loading="eager"
            />
          </div>
          
          {/* Contenuto testuale SOTTO l'hero (fuori dal container 100vh) */}
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-24 text-center" style={{ backgroundColor: section.background_color || "#F9F8F6" }}>
            {textBlocks.map(block => renderBlock(block))}
          </div>
        </section>
      );
    }

    // Default: Custom section - render all blocks IN ORDER
    if (sortedBlocks.length === 0) {
      return null;
    }
    
    // Check if all blocks are empty (no content)
    const hasVisibleContent = sortedBlocks.some(b => {
      if (b.block_type === "gallery") {
        const images = b.gallery_images || [];
        const validImages = images.filter(i => i.url && i.url.trim() !== "");
        return validImages.length > 0 || b.gallery_title || b.gallery_description;
      }
      if (b.block_type === "image") return b.image_url && b.image_url.trim() !== "";
      return true; // Other block types have content
    });
    
    if (!hasVisibleContent) {
      return null; // Don't render section with no visible content
    }
    
    return (
      <section key={section.section_id} id={sectionId}
        className={section.padding || "py-24"} style={{ backgroundColor: section.background_color || "#F9F8F6" }}>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            {sortedBlocks.map(block => renderBlock(block))}
          </div>
          <div className="clear-both" />
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: settings.colors?.background || "#F9F8F6" }}>
      <div className="noise-overlay" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#F9F8F6]/95 backdrop-blur-sm">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            {logoConfig.logo_url ? (
              <img 
                src={logoConfig.logo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${logoConfig.logo_url}` : logoConfig.logo_url} 
                alt="Suite Lucina" 
                style={{ height: `${logoConfig.logo_size}px` }}
                className="object-contain"
              />
            ) : (
              <img src="https://customer-assets.emergentagent.com/job_lucina-suite/artifacts/zplyxqc9_logo.png" alt="Suite Lucina" className="h-10 md:h-12" />
            )}
          </a>
          <div className="hidden md:flex items-center gap-8">
            {sections
              .filter(s => s.is_active !== false && s.show_in_nav === true)
              .map(s => {
                const sectionId = s.section_name.toLowerCase().replace(/\s+/g, '-');
                // Use menu_label if available, otherwise fall back to section_name
                const displayName = s.menu_label?.[language] || s.menu_label?.en || s.section_name;
                return (
                  <a 
                    key={s.section_id}
                    href={`#${sectionId}`} 
                    className="font-body text-sm uppercase tracking-widest text-[#78716C] hover:text-[#1C1917] transition-colors"
                  >
                    {displayName}
                  </a>
                );
              })}
          </div>
          <div className="flex items-center gap-5">
            <div className="relative mr-1" data-testid="language-selector">
              <button onClick={() => setShowLangDropdown(!showLangDropdown)} className="flex items-center gap-2 text-sm transition-colors" data-testid="language-toggle">
                <Globe size={18} className="text-[#78716C]" /><span className="hidden md:inline text-[#D4AF37] hover:text-[#B8962E]">{LANGUAGES[language].name}</span>
              </button>
              {showLangDropdown && (
                <div className="language-dropdown" data-testid="language-dropdown">
                  {LANGUAGE_ORDER.map(lang => (
                    <button key={lang} onClick={() => { setLanguage(lang); localStorage.setItem('language', lang); setShowLangDropdown(false); }} className={language === lang ? "bg-[#F9F8F6]" : ""} data-testid={`lang-${lang}`}>
                      {LANGUAGES[lang].name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <a href="/admin" className="text-[#78716C] hover:text-[#1C1917] transition-colors" data-testid="admin-link" title="Admin"><User size={18} /></a>
            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle"><Menu size={24} /></button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#F9F8F6] border-t border-[#E7E5E4] px-6 py-4">
            {sections
              .filter(s => s.is_active !== false && s.show_in_nav === true)
              .map(s => {
                const sectionId = s.section_name.toLowerCase().replace(/\s+/g, '-');
                // Use menu_label if available, otherwise fall back to section_name
                const displayName = s.menu_label?.[language] || s.menu_label?.en || s.section_name;
                return (
                  <a 
                    key={s.section_id}
                    href={`#${sectionId}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-3 text-sm uppercase tracking-widest text-[#78716C]"
                  >
                    {displayName}
                  </a>
                );
              })}
          </div>
        )}
      </nav>

      {/* MAIN CONTENT: Placeholder stabile per prevenire layout shift */}
      {/* CRITICAL: min-h-[calc(100vh-200px)] garantisce che il main occupi sempre spazio */}
      <main className="flex-1 min-h-[calc(100vh-200px)]">
        {isPageLoading ? (
          /* SKELETON LOADER: Mantiene altezza stabile durante il caricamento */
          <div className="min-h-[calc(100vh-200px)] pt-20" style={{ backgroundColor: "#F9F8F6" }}>
            {/* Hero skeleton */}
            <div className="w-full h-[50vh] md:h-[70vh] bg-gradient-to-b from-[#E7E5E4] to-[#F9F8F6] animate-pulse" />
            {/* Content skeleton */}
            <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16">
              <div className="max-w-2xl mx-auto space-y-6">
                <div className="h-12 bg-[#E7E5E4] rounded animate-pulse w-3/4 mx-auto" />
                <div className="h-4 bg-[#E7E5E4] rounded animate-pulse w-full" />
                <div className="h-4 bg-[#E7E5E4] rounded animate-pulse w-5/6" />
                <div className="h-4 bg-[#E7E5E4] rounded animate-pulse w-4/6" />
              </div>
            </div>
          </div>
        ) : sections.length === 0 ? (
          /* FALLBACK: Quando non ci sono sezioni da mostrare */
          <div className="min-h-[calc(100vh-200px)] pt-20 flex items-center justify-center" style={{ backgroundColor: "#F9F8F6" }}>
            <div className="text-center text-[#78716C]">
              <p className="text-lg">Loading content...</p>
            </div>
          </div>
        ) : (
          /* CONTENUTO REALE: Solo quando i dati sono pronti */
          sections.map(section => renderSection(section))
        )}
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 border-t border-[#E7E5E4] bg-[#1C1917] text-white">
        <div className="max-w-screen-2xl mx-auto px-0">
          {/* Desktop: 3 columns | Mobile: Legal info above, links row below */}
          <div className="hidden md:grid md:grid-cols-3 gap-8 mb-8">
            {/* Left Column - Legal Information */}
            <div className="text-left">
              {logoConfig.show_in_footer && (
                logoConfig.logo_url ? (
                  <img 
                    src={logoConfig.logo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${logoConfig.logo_url}` : logoConfig.logo_url} 
                    alt="Suite Lucina" 
                    style={{ height: `${logoConfig.logo_size}px` }}
                    className="mb-4 invert object-contain"
                  />
                ) : (
                  <img src="https://customer-assets.emergentagent.com/job_lucina-suite/artifacts/zplyxqc9_logo.png" alt="Suite Lucina" className="h-10 mb-4 invert" />
                )
              )}
              <p 
                className="text-sm text-white/60"
                dangerouslySetInnerHTML={{ __html: (legal.legal_info?.text || legal.legal_info?.it || legal.legal_info?.en || "").replace(/\n/g, '<br>') }}
              />
            </div>
            
            {/* Center Column - Policy */}
            <div className="text-center">
              {(legal.policy_items || []).map((item, index) => {
                const label = item.label?.text || item.label?.it || item.label?.en || item.label;
                if (!label || typeof label === 'object') return null;
                return (
                  <button 
                    key={item.id || index}
                    onClick={() => setShowLegalModal({ type: 'policy', item })}
                    className="text-sm text-white/60 hover:text-white transition-colors block mb-2 mx-auto"
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            
            {/* Right Column - Contact */}
            <div className="text-right">
              {(legal.contact_items || []).map((item, index) => {
                const label = item.label?.text || item.label?.it || item.label?.en || item.label;
                if (!label || typeof label === 'object') return null;
                return (
                  <button 
                    key={item.id || index}
                    onClick={() => setShowLegalModal({ type: 'contact', item })}
                    className="text-sm text-white/60 hover:text-white transition-colors block mb-2 ml-auto"
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Mobile layout */}
          <div className="md:hidden mb-8">
            {/* Logo and Legal Info */}
            <div className="text-center mb-6">
              {logoConfig.show_in_footer && (
                logoConfig.logo_url ? (
                  <img 
                    src={logoConfig.logo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${logoConfig.logo_url}` : logoConfig.logo_url} 
                    alt="Suite Lucina" 
                    style={{ height: `${logoConfig.logo_size}px` }}
                    className="mb-4 invert object-contain mx-auto"
                  />
                ) : (
                  <img src="https://customer-assets.emergentagent.com/job_lucina-suite/artifacts/zplyxqc9_logo.png" alt="Suite Lucina" className="h-10 mb-4 invert mx-auto" />
                )
              )}
              <p 
                className="text-sm text-white/60"
                dangerouslySetInnerHTML={{ __html: (legal.legal_info?.text || legal.legal_info?.it || legal.legal_info?.en || "").replace(/\n/g, '<br>') }}
              />
            </div>
            
            {/* Links Row: Policy (left) | centered | Contact (right) */}
            <div className="flex justify-between items-start">
              {/* Left - Policy items */}
              <div className="text-left">
                {(legal.policy_items || []).map((item, index) => {
                  const label = item.label?.text || item.label?.it || item.label?.en || item.label;
                  if (!label || typeof label === 'object') return null;
                  return (
                    <button 
                      key={item.id || index}
                      onClick={() => setShowLegalModal({ type: 'policy', item })}
                      className="text-sm text-white/60 hover:text-white transition-colors block mb-1"
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              
              {/* Right - Contact items */}
              <div className="text-right">
                {(legal.contact_items || []).map((item, index) => {
                  const label = item.label?.text || item.label?.it || item.label?.en || item.label;
                  if (!label || typeof label === 'object') return null;
                  return (
                    <button 
                      key={item.id || index}
                      onClick={() => setShowLegalModal({ type: 'contact', item })}
                      className="text-sm text-white/60 hover:text-white transition-colors block mb-1"
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/10 text-center">
            {/* Copyright - anno automatico + testo personalizzabile */}
            <p className="text-sm text-white/40">
              © {new Date().getFullYear()} {(() => {
                const rawText = legal.copyright?.text || legal.copyright?.it || legal.copyright?.en || "";
                // Pulisci il testo: rimuovi simbolo ©, {{year}}, e trim
                const cleanText = rawText.replace(/©\s*/g, '').replace(/\{\{year\}\}/g, '').replace(/^\d{4}\s*/g, '').trim();
                return cleanText || "All rights reserved.";
              })()}
            </p>
          </div>
        </div>
      </footer>

      {/* Legal Modal (Policy/Contact) */}
      {showLegalModal && (
        <div className="fixed inset-0 bg-black/80 z-[10001] flex items-center justify-center p-4" onClick={() => setShowLegalModal(null)}>
          <div className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto p-8 relative rounded-lg" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowLegalModal(null)} className="absolute top-4 right-4 text-[#78716C] hover:text-[#1C1917]"><X size={24} /></button>
            <h2 className="font-heading text-3xl mb-6">{(() => {
              const label = showLegalModal.item.label;
              if (!label) return "";
              if (label.text) return label.text;
              // Get longest content (real content vs placeholder)
              const values = [label.it, label.en, label.es, label.fr, label.de].filter(v => typeof v === 'string' && v.length > 0);
              return values.length > 0 ? values.reduce((a, b) => (a.length > b.length ? a : b), "") : "";
            })()}</h2>
            <div 
              className="prose prose-sm text-[#78716C] [&_strong]:text-[#44403C] [&_b]:text-[#44403C]"
              style={{ whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{ __html: (() => {
                const content = showLegalModal.item.content;
                if (!content) return "";
                if (content.text) return content.text.replace(/\n/g, '<br>');
                // Get longest content (real content vs placeholder)
                const values = [content.it, content.en, content.es, content.fr, content.de].filter(v => typeof v === 'string' && v.length > 0);
                const best = values.length > 0 ? values.reduce((a, b) => (a.length > b.length ? a : b), "") : "";
                return best.replace(/\n/g, '<br>');
              })() }}
            />
          </div>
        </div>
      )}

      {/* Lightbox - SCOPED to current gallery block images */}
      {lightboxOpen && lightboxImages.length > 0 && (
        <div className="lightbox-overlay" onClick={closeLightbox} data-testid="lightbox">
          <button className="lightbox-close" onClick={closeLightbox} data-testid="lightbox-close"><X size={32} /></button>
          <button className="lightbox-nav lightbox-prev" onClick={(e) => { e.stopPropagation(); navigateLightbox("prev"); }} data-testid="lightbox-prev"><ChevronLeft size={32} /></button>
          <img 
            src={lightboxImages[lightboxIndex]?.url?.startsWith("/api") 
              ? `${process.env.REACT_APP_BACKEND_URL}${lightboxImages[lightboxIndex]?.url}` 
              : lightboxImages[lightboxIndex]?.url} 
            alt={lightboxImages[lightboxIndex]?.alt?.[language] || ""} 
            className="lightbox-image" 
            onClick={(e) => e.stopPropagation()} 
          />
          <button className="lightbox-nav lightbox-next" onClick={(e) => { e.stopPropagation(); navigateLightbox("next"); }} data-testid="lightbox-next"><ChevronRight size={32} /></button>
          {/* Image counter */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded">
            {lightboxIndex + 1} / {lightboxImages.length}
          </div>
        </div>
      )}
    </div>
  );
}
